import React, { useState } from 'react';
import './header-right-bar.scss';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../../store';
import { VersionButtonContainer } from '../../version-button/version-button-container';
import PolicyManagerIcon from '@svg/configuration.svg?react';
import PolicySettingsIcon from '@svg/filter_24.svg?react';
import SmartPolicyIcon from '@svg/smart-indication.svg?react';
import { PolicyManagerContainer } from '../policy-manager/policy-manager-container';
import { SmartProtectionModal } from '../smart-protection-modal/smart-protection-modal';
import { PopoverGotIt } from '@src/components/shared/popover/popover-got-it';
import { EventType } from '@src/dtos/analytics.dto';
import { Dropdown, MenuProps } from 'antd';
import { POLICY_SETTINGS_POPUP_SEEN } from '@src/utils/constants';

export const HeaderRightBar: React.FC = observer(() => {
    const { fwVersionStore, customerStore } = useStores();
    const [policyManagerOpen, setPolicyManagerOpen] = useState(false);
    const [smartProtectionModalOpen, setSmartProtectionModalOpen] = useState(false);

    /**
     * One-time onboarding popup — shown on first-ever Policy tab visit.
     * State is initialised from localStorage so it survives re-renders but
     * resets to visible for users who have never seen it.
     */
    const [showOnboardingPopup, setShowOnboardingPopup] = useState(
        () => !localStorage.getItem(POLICY_SETTINGS_POPUP_SEEN)
    );

    const dismissOnboardingPopup = () => {
        localStorage.setItem(POLICY_SETTINGS_POPUP_SEEN, 'true');
        setShowOnboardingPopup(false);
    };

    const closePolicyManagerPanel = () => {
        setPolicyManagerOpen(false);
    };

    const openPolicyManagerPanel = () => {
        setPolicyManagerOpen(true);
        fwVersionStore.sendGeneralEvent(EventType.LinkClicked, { button: 'policyManagerIcon' });
    };

    const openSmartProtectionModal = () => {
        fwVersionStore.sendGeneralEvent(EventType.LinkClicked, { button: 'smartProtection' });
        setSmartProtectionModalOpen(true);
    };

    const closeSmartProtectionModal = () => {
        setSmartProtectionModalOpen(false);
    };

    const isSmartProtectionDisabled =
        fwVersionStore.isVersionDeactivate ||
        !!fwVersionStore.pendingVersion ||
        customerStore.isSmartProtectionRunning;

    const menuItems: MenuProps['items'] = [
        {
            key: 'smart-protection',
            label: 'Smart protection',
            icon: <SmartPolicyIcon className="policy-settings-menu-icon" />,
            disabled: isSmartProtectionDisabled,
            onClick: openSmartProtectionModal
        },
        {
            key: 'policy-manager',
            label: 'Policy manager',
            icon: <PolicyManagerIcon className="policy-settings-menu-icon" />,
            onClick: openPolicyManagerPanel
        }
    ];

    return <div className="header-right-bar">
        <div className="right-area">
            <PolicyManagerContainer visible={policyManagerOpen} close={closePolicyManagerPanel} />
            <SmartProtectionModal
                open={smartProtectionModalOpen}
                onClose={closeSmartProtectionModal}
            />
            <VersionButtonContainer />
            {fwVersionStore.fwVersions && (
                <PopoverGotIt
                    open={showOnboardingPopup}
                    placement="bottomRight"
                    overlayClassName="policy-settings-popup"
                    title="Policy settings"
                    text="Manage your policy versions, view policy summary, apply smart protection and more."
                    onClickGotIt={dismissOnboardingPopup}
                >
                    <Dropdown menu={{ items: menuItems }} trigger={['click']}
                              overlayClassName="policy-settings-dropdown">
                        <div className="policy-settings-btn">
                            <PolicySettingsIcon className="policy-settings-icon"/>
                            Policy settings
                        </div>
                    </Dropdown>
                </PopoverGotIt>
            )}
        </div>
    </div>;
});
